import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { isNative } from '../../lib/platform';
import PortalLayout from './PortalLayout';
// invoice_date / quote_date / due_date / payment_date are INSTANTS (api#838)
// and render as the TENANT's day via formatInstantTenantDay — their UTC
// prefix read a day late for anything written after 18:00 MDT, on the
// screen the CUSTOMER reads. valid_until is a calendar day and stays on
// formatCalendarDate.
//
// Before that they were treated as calendar days: `new Date()` read them as
// UTC midnight and this portal showed every one of them a day early. The local
// fmtDate that fixed that then ended in a bare toLocaleDateString(), so the
// FORMAT tracked the reader's own device instead: one customer saw 3/23/2026
// and another 23/03/2026 for the same day, with nothing on the page to say
// which. formatCalendarDate's default renders a month NAME, which no device
// can reorder into a different day.
import { formatCalendarDate, formatInstantTenantDay } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency, fmtUnitRate } from '../../lib/display-labels';

export default function PortalQuoteDetail() {
  const timeZone = useTenantTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [downloading, setDownloading] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['portal-quote', id],
    queryFn: () => api.portal.quote(id!).then(r => r.data.data),
    enabled: !!id,
  });

  const acceptMutation = useMutation({
    mutationFn: () => api.portal.acceptQuote(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-quote', id] });
      queryClient.invalidateQueries({ queryKey: ['portal-quotes'] });
      queryClient.invalidateQueries({ queryKey: ['portal-dashboard'] });
    },
  });

  const declineMutation = useMutation({
    mutationFn: () => api.portal.declineQuote(id!, declineReason || undefined),
    onSuccess: () => {
      setShowDeclineModal(false);
      queryClient.invalidateQueries({ queryKey: ['portal-quote', id] });
      queryClient.invalidateQueries({ queryKey: ['portal-quotes'] });
      queryClient.invalidateQueries({ queryKey: ['portal-dashboard'] });
    },
  });

  const [saved, setSaved] = useState(false);

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const handleDownload = async () => {
    setDownloading(true);
    setSaved(false);
    try {
      const res = await api.portal.quotePdf(id!);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const filename = `${data?.quote?.quote_number || 'quote'}.pdf`;

      if (isNative) {
        const base64 = await blobToBase64(blob);
        await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: Directory.Documents,
        });
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('PDF download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  const quote = data?.quote;
  const lineItems = data?.lineItems || [];
  // Money on a document the CUSTOMER reads. `parseFloat(n || '0')` turned an
  // amount the server did not send into $0.00 — an invoice total that is
  // genuinely unknown is not an invoice for nothing. @see fmtCurrency.
  const fmt = (n: number | string | null | undefined) =>
    fmtCurrency(n, quote?.currency || 'CAD');
  const isSent = quote?.status === 'sent';

  return (
    <PortalLayout>
      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 size={28} className="animate-spin text-fieldiq-accent" />
        </div>
      ) : !quote ? (
        <p className="text-center text-fieldiq-muted py-20">Quote not found</p>
      ) : (
        <div className="space-y-4 animate-fade-in">
          <button onClick={() => navigate('/portal/quotes')} className="flex items-center gap-1 text-fieldiq-muted hover:text-white text-sm">
            <ArrowLeft size={26} /> Back to Quotes
          </button>

          {/* Quote Header */}
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-white font-bold text-xl">{quote.quote_number}</h2>
                <p className="text-fieldiq-muted text-sm mt-1">
                  Issued: {(formatInstantTenantDay(quote.quote_date, timeZone) ?? '—')}
                  {quote.valid_until && ` · Valid until: ${(formatCalendarDate(quote.valid_until) ?? '—')}`}
                </p>
              </div>
              <StatusBadge status={quote.status} />
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-fieldiq-muted text-xs mb-1">From</p>
                <p className="text-white font-medium">{quote.company_name}</p>
              </div>
              <div>
                <p className="text-fieldiq-muted text-xs mb-1">To</p>
                <p className="text-white font-medium">{quote.customer_name}</p>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
            <h3 className="text-white font-semibold text-sm mb-3">Line Items</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-fieldiq-muted text-xs border-b border-fieldiq-border">
                    <th className="text-left py-2 pr-4">Description</th>
                    <th className="text-right py-2 px-2">Qty</th>
                    <th className="text-right py-2 px-2">Rate</th>
                    <th className="text-right py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li: any, i: number) => (
                    <tr key={li.id || i} className="border-b border-fieldiq-border/50">
                      <td className="py-2.5 pr-4 text-white">{li.description || li.line_item_type}</td>
                      <td className="py-2.5 px-2 text-right text-fieldiq-muted">{li.quantity}</td>
                      <td className="py-2.5 px-2 text-right text-fieldiq-muted">{fmtUnitRate(li.rate, quote?.currency || 'CAD')}</td>
                      <td className="py-2.5 text-right text-white font-medium">{fmt(li.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 pt-3 border-t border-fieldiq-border space-y-1.5 text-sm">
              <div className="flex justify-between text-fieldiq-muted">
                <span>Subtotal</span>
                <span>{fmt(quote.subtotal)}</span>
              </div>
              {quote.tax_rate_1_amount && parseFloat(quote.tax_rate_1_amount) > 0 && (
                <div className="flex justify-between text-fieldiq-muted">
                  <span>{quote.tax_rate_1_label || 'Tax'}</span>
                  <span>{fmt(quote.tax_rate_1_amount)}</span>
                </div>
              )}
              {quote.tax_rate_2_amount && parseFloat(quote.tax_rate_2_amount) > 0 && (
                <div className="flex justify-between text-fieldiq-muted">
                  <span>{quote.tax_rate_2_label || 'Tax'}</span>
                  <span>{fmt(quote.tax_rate_2_amount)}</span>
                </div>
              )}
              <div className="flex justify-between text-white font-bold text-lg pt-1 border-t border-fieldiq-border">
                <span>Total</span>
                <span>{fmt(quote.total_amount)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {quote.notes && (
            <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
              <h3 className="text-white font-semibold text-sm mb-2">Notes</h3>
              <p className="text-fieldiq-muted text-sm whitespace-pre-wrap">{quote.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-fieldiq-card border border-fieldiq-border text-white font-semibold rounded-xl text-sm disabled:opacity-50"
            >
              {downloading ? <Loader2 size={16} className="animate-spin" /> : saved ? <CheckCircle size={16} /> : <Download size={16} />}
              {downloading ? 'Downloading...' : saved ? 'Saved to Documents' : 'Download PDF'}
            </button>
          </div>

          {isSent && (
            <div className="flex gap-3">
              <button
                onClick={() => acceptMutation.mutate()}
                disabled={acceptMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-600 text-white font-bold rounded-xl text-sm hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {acceptMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                Accept Quote
              </button>
              <button
                onClick={() => setShowDeclineModal(true)}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-600/20 border border-red-500/30 text-red-400 font-semibold rounded-xl text-sm hover:bg-red-600/30 transition-colors"
              >
                <XCircle size={16} />
                Decline
              </button>
            </div>
          )}

          {(acceptMutation.isError || declineMutation.isError) && (
            <p className="text-red-400 text-sm text-center">
              {((acceptMutation.error || declineMutation.error) as any)?.response?.data?.error || 'Action failed'}
            </p>
          )}

          {acceptMutation.isSuccess && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
              <CheckCircle size={24} className="text-green-400 mx-auto mb-2" />
              <p className="text-green-400 font-semibold">Quote Accepted</p>
            </div>
          )}
        </div>
      )}

      {/* Decline Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-bold text-white mb-2">Decline Quote</h3>
            <p className="text-sm text-fieldiq-muted mb-4">Optionally provide a reason:</p>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Reason for declining (optional)"
              rows={3}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeclineModal(false)}
                className="flex-1 py-2.5 bg-fieldiq-dark text-white font-medium rounded-lg text-sm"
                disabled={declineMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => declineMutation.mutate()}
                disabled={declineMutation.isPending}
                className="flex-1 py-2.5 bg-red-500 text-white font-semibold rounded-lg text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {declineMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    sent: 'bg-blue-500/20 text-blue-400',
    accepted: 'bg-green-500/20 text-green-400',
    declined: 'bg-red-500/20 text-red-400',
    expired: 'bg-gray-500/20 text-gray-400',
  };
  return (
    <span className={`text-xs font-semibold px-2 py-1 rounded ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {status?.toUpperCase()}
    </span>
  );
}
